import React, { useEffect, useState } from "react";
import AdminSectionHeader from "@/components/admin/AdminSectionHeader";
import { api, formatApiError } from "@/lib/api";
import CTAButton from "@/components/app/CTAButton";
import { Save } from "lucide-react";

export default function AdminSettings() {
    const [settings, setSettings] = useState([]);
    const [error, setError] = useState("");
    const [saving, setSaving] = useState({});
    const [drafts, setDrafts] = useState({});

    useEffect(() => {
        api.get("/admin/settings").then(({ data }) => setSettings(data)).catch((e) => setError(formatApiError(e)));
    }, []);

    async function save(key) {
        setSaving((s) => ({ ...s, [key]: true }));
        try {
            await api.put(`/admin/settings/${encodeURIComponent(key)}`, { value: drafts[key] });
            setSettings((all) => all.map((s) => (s.key === key ? { ...s, value: drafts[key] } : s)));
            setDrafts((d) => { const n = { ...d }; delete n[key]; return n; });
        } catch (e) {
            setError(formatApiError(e));
        } finally {
            setSaving((s) => ({ ...s, [key]: false }));
        }
    }

    const grouped = settings.reduce((acc, s) => {
        (acc[s.category] = acc[s.category] || []).push(s);
        return acc;
    }, {});

    return (
        <>
            <AdminSectionHeader title="Paramètres globaux" description="Délais, cooldowns, frais plateforme — modifiables sans toucher au code." />
            {error && <div className="mb-4 rounded-lg border border-md-danger/30 bg-md-danger/10 px-4 py-2 text-sm text-md-danger">{error}</div>}
            <div className="space-y-6">
                {Object.entries(grouped).map(([cat, items]) => (
                    <div key={cat} className="rounded-2xl border border-md-border bg-md-surface p-6">
                        <p className="md-overline">{cat}</p>
                        <div className="mt-4 divide-y divide-md-border">
                            {items.map((s) => {
                                const draftVal = drafts[s.key] !== undefined ? drafts[s.key] : s.value;
                                const dirty = drafts[s.key] !== undefined && drafts[s.key] !== s.value;
                                const isNumber = typeof s.value === "number";
                                return (
                                    <div key={s.key} className="flex flex-col gap-3 py-4 sm:flex-row sm:items-center">
                                        <div className="min-w-0 flex-1">
                                            <p className="font-mono text-xs font-semibold text-md-text">{s.key}</p>
                                            {s.description && (
                                                <p className="mt-1 text-xs text-md-text-2">{s.description}</p>
                                            )}
                                        </div>
                                        <input
                                            type={isNumber ? "number" : "text"}
                                            value={String(draftVal ?? "")}
                                            onChange={(e) => {
                                                const v = isNumber ? Number(e.target.value) : e.target.value;
                                                setDrafts((d) => ({ ...d, [s.key]: v }));
                                            }}
                                            data-testid={`setting-input-${s.key}`}
                                            className="w-full rounded-lg border border-md-border bg-md-bg px-3 py-2 text-sm text-md-text outline-none focus:border-md-primary sm:w-48"
                                        />
                                        <CTAButton
                                            size="sm"
                                            icon={<Save className="h-3.5 w-3.5" />}
                                            disabled={!dirty}
                                            loading={!!saving[s.key]}
                                            onClick={() => save(s.key)}
                                            testId={`setting-save-${s.key}`}
                                        >
                                            Enregistrer
                                        </CTAButton>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                ))}
            </div>
        </>
    );
}
